/// <reference lib="es2023.intl" />

const GROUPED: Intl.NumberFormatOptions = { useGrouping: "always" };

export const EMPTY_VALUE = "—";

const MONEY = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  ...GROUPED,
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const MONEY_EXACT = new Intl.NumberFormat("es-ES", {
  style: "currency",
  currency: "EUR",
  ...GROUPED,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const INTEGER = new Intl.NumberFormat("es-ES", {
  ...GROUPED,
  maximumFractionDigits: 0,
});

const PERCENT = new Intl.NumberFormat("es-ES", {
  style: "percent",
  ...GROUPED,
  maximumFractionDigits: 1,
});

const currencyFormat = (currency: string, cents: boolean) =>
  currency === "EUR"
    ? cents
      ? MONEY_EXACT
      : MONEY
    : new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency,
        ...GROUPED,
        minimumFractionDigits: cents ? 2 : 0,
        maximumFractionDigits: cents ? 2 : 0,
      });

type Numeric = number | string | null | undefined;

export const toNumber = (value: Numeric): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = typeof value === "string" ? Number.parseFloat(value) : value;
  return Number.isFinite(parsed) ? parsed : null;
};

export const NUMBER_LOCALE = "es-ES";

const MONEY_INPUT_FORMAT: Intl.NumberFormatOptions = {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 2,
  ...GROUPED,
};

const PERCENT_INPUT_FORMAT: Intl.NumberFormatOptions = {
  style: "percent",
  maximumFractionDigits: 4,
  ...GROUPED,
};

const unitOf = (options: Intl.NumberFormatOptions): string =>
  new Intl.NumberFormat(NUMBER_LOCALE, options)
    .formatToParts(0)
    .find((part) => part.type === "currency" || part.type === "percentSign")?.value ?? "";

export interface DecimalInputFormat {
  scale: number;
  minimumFractionDigits: number;
  unit: string;
}

export const MONEY_DECIMAL_INPUT: DecimalInputFormat = {
  scale: 2,
  minimumFractionDigits: 2,
  unit: unitOf(MONEY_INPUT_FORMAT),
};

export const PERCENT_DECIMAL_INPUT: DecimalInputFormat = {
  scale: 2,
  minimumFractionDigits: 0,
  unit: unitOf(PERCENT_INPUT_FORMAT),
};

export const formatMoney = (value: Numeric, currency = "EUR"): string => {
  const amount = toNumber(value);
  return amount === null ? EMPTY_VALUE : currencyFormat(currency, false).format(amount);
};

export const formatMoneyExact = (value: Numeric, currency = "EUR"): string => {
  const amount = toNumber(value);
  return amount === null ? EMPTY_VALUE : currencyFormat(currency, true).format(amount);
};

export const formatInteger = (value: Numeric): string => {
  const amount = toNumber(value);
  return amount === null ? EMPTY_VALUE : INTEGER.format(amount);
};

export const formatPercent = (value: Numeric): string => {
  const amount = toNumber(value);
  return amount === null ? EMPTY_VALUE : PERCENT.format(amount / 100);
};

export const formatPercentFromRatio = (value: number, total: number): string =>
  formatPercent(total === 0 ? 0 : (value / total) * 100);

export const formatMoneyCompact = (value: Numeric): string => {
  const amount = toNumber(value);
  if (amount === null) return EMPTY_VALUE;
  const absolute = Math.abs(amount);
  const sign = amount < 0 ? "-" : "";
  if (absolute >= 1_000_000) {
    return `${sign}${INTEGER.format(Math.round((absolute / 1_000_000) * 10) / 10)}M €`;
  }
  if (absolute >= 1_000) return `${sign}${INTEGER.format(absolute / 1_000)}k €`;
  return formatMoney(amount);
};

const entityReference = (prefix: string, uuid: string | null): string =>
  uuid ? `#${prefix}-${uuid.substring(0, 4).toUpperCase()}` : "-";

export const formatInvestmentReference = (uuid: string | null): string =>
  entityReference("INV", uuid);

export const formatProposalReference = (uuid: string | null): string => entityReference("PR", uuid);

/**
 * Format a number string for display with Spanish thousand separators
 * Converts internal format (1234.56) to display format (1.234,56)
 * Preserves exact decimal places from the input (unlike formatNumberInput)
 */
export const formatNumberDisplay = (value: string): string => {
  if (!value) return "";
  const [integerPart = "", ...rest] = value.split(".");
  return [integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, "."), ...rest].join(",");
};

/**
 * Format a number string for display with Spanish locale during typing.
 * Converts internal format (1234.56) to display format (1.234,56).
 *
 * Preserves exactly what the user typed — including trailing zeros in the
 * decimal part ("46.0" → "46,0") and a lone "0" ("0" → "0"). Does NOT go
 * through parseFloat so digit-typing flow stays lossless.
 */
export const formatNumberInput = (value: string | null | undefined): string => {
  if (value == null || value === "") return "";

  const dotIdx = value.indexOf(".");
  const integerStr = dotIdx === -1 ? value : value.slice(0, dotIdx);
  const decimalStr = dotIdx === -1 ? null : value.slice(dotIdx + 1);

  if (integerStr !== "" && !/^\d+$/.test(integerStr)) return "";
  if (decimalStr !== null && !/^\d*$/.test(decimalStr)) return "";

  let displayInt = "";
  if (integerStr !== "") {
    // Strip leading zeros but keep a lone "0" (so "0" stays "0", "007" → "7")
    const trimmed = integerStr.replace(/^0+(?=\d)/, "");
    displayInt = trimmed.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  }

  if (dotIdx === -1) return displayInt;

  // Decimal part: preserve exactly what was typed, capped at 2 digits.
  const decimal = (decimalStr ?? "").slice(0, 2);
  return `${displayInt},${decimal}`;
};

const DATE_SHORT = new Intl.DateTimeFormat(NUMBER_LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
});

const DATE_TIME_SHORT = new Intl.DateTimeFormat(NUMBER_LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const MONTH_LONG = new Intl.DateTimeFormat(NUMBER_LOCALE, { month: "long" });

const shortMonth = (text: string) => text.replace(/\./g, "").replace(/\bsept\b/, "sep");

const dateOf = (value: string | Date | null | undefined): Date | null => {
  if (!value) return null;
  const date = typeof value === "string" ? new Date(value) : value;
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDateShort = (value: string | Date | null | undefined): string => {
  const date = dateOf(value);
  return date === null ? EMPTY_VALUE : shortMonth(DATE_SHORT.format(date));
};

export const formatDateTimeShort = (value: string | Date | null | undefined): string => {
  const date = dateOf(value);
  return date === null ? EMPTY_VALUE : shortMonth(DATE_TIME_SHORT.format(date));
};

const DATE_TIME_EXACT = new Intl.DateTimeFormat(NUMBER_LOCALE, {
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
});

export const formatDateTimeExact = (value: string | Date | null | undefined): string => {
  const date = dateOf(value);
  return date === null ? EMPTY_VALUE : shortMonth(DATE_TIME_EXACT.format(date));
};

export const formatMonthHeading = (value: string | Date | null | undefined): string => {
  const date = dateOf(value);
  if (date === null) return EMPTY_VALUE;
  const month = MONTH_LONG.format(date);
  const heading = month.charAt(0).toUpperCase() + month.slice(1);
  return date.getFullYear() === new Date().getFullYear()
    ? heading
    : `${heading} ${date.getFullYear()}`;
};

/**
 * Format date relative to now (e.g., "Hoy", "Ayer", "Hace 3 días")
 * @param dateString - ISO date string
 * @param t - Translation function for i18n support
 */
export const formatRelativeDate = (
  dateString: string | null | undefined,
  t: (key: string, fallback?: string) => string,
): string => {
  if (!dateString) return "-";

  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return t("hub.activity.today", "Hoy");
    } else if (diffDays === 1) {
      return t("hub.activity.yesterday", "Ayer");
    } else if (diffDays < 7) {
      const text = t("hub.activity.days_ago", "Hace {{count}} días");
      return text.replace("{{count}}", String(diffDays));
    } else {
      return formatDateShort(dateString);
    }
  } catch {
    return "-";
  }
};

/**
 * Get initials from a name (e.g., "John Doe" -> "JD")
 */
export const getInitials = (name: string | null | undefined): string => {
  if (!name) return "??";
  const words = name.trim().split(" ").filter(Boolean);
  if (words.length >= 2) {
    return `${words[0]?.[0]}${words[1]?.[0]}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
};

export const getFileExtension = (contentType: string): string => {
  const typeMap: Record<string, string> = {
    "application/pdf": "PDF",
    "image/png": "PNG",
    "image/jpeg": "JPG",
    "image/jpg": "JPG",
  };
  return typeMap[contentType] || contentType.split("/")[1]?.toUpperCase() || "FILE";
};

/**
 * Format current date with weekday (e.g., "Miércoles, 26 de febrero de 2026")
 * Capitalizes the first letter, lowercases the rest.
 */
export const formatCurrentDateLong = (locale: string): string => {
  const now = new Date();
  const formatted = now.toLocaleDateString(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  return formatted.charAt(0).toUpperCase() + formatted.slice(1).toLowerCase();
};
